import { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { useGetKimThreadFile } from '@/api/queries/useGetKimThreadFile';
import { Icon } from '@/new/components/icon';
import { PDFViewer } from '@/new/components/pdf-viewer';
import { ChunkDrawerContentShell } from '@/new/components/reference-content/chunk-drawer-content-shell';
import { DrawerErrorState } from '@/new/components/reference-content/drawer-error-state';
import { BoundingBoxWithId } from '@/types';

interface ThreadFileChunkDrawerContentProps {
  chunkId?: string;
  fileId?: string;
}

export function ThreadFileChunkDrawerContent({ chunkId, fileId }: ThreadFileChunkDrawerContentProps) {
  const { matterId, subsetId, threadId } = useParams();

  const { data, isLoading, error } = useGetKimThreadFile({ matterId, subsetId, threadId, chunkId, fileId });

  const highlight: BoundingBoxWithId | null = useMemo(
    () => (data?.chunk ? { ...data.chunk.boundingBox, id: data.chunk.id } : null),
    [data],
  );
  const fileMetadata = useMemo(
    () => (data ? { name: data.name, extension: data.extension, pageDimensions: data.pageDimensions } : undefined),
    [data],
  );
  const threadFile = useMemo(
    () =>
      data && matterId && subsetId && threadId
        ? { matterId, subsetId, threadId, fileId: data.id, fileName: data.name }
        : undefined,
    [data, matterId, subsetId, threadId],
  );

  if (data?.chunk) {
    return (
      <ChunkDrawerContentShell
        isLoading={isLoading}
        error={error}
        errorLabel="thread file chunk"
        viewer={
          highlight ? (
            <div className="flex h-full flex-col">
              <div className="flex-grow overflow-hidden">
                <div className="relative h-full">
                  <PDFViewer
                    docId={data.id}
                    fileUrl={data.url}
                    fileMetadata={fileMetadata}
                    highlights={[highlight]}
                    highlightedFactId={data.chunk.id}
                    hideFeatureButtons={true}
                    hideSnippingTool={true}
                    fileName={data.name}
                    threadFile={threadFile}
                  />
                </div>
              </div>
            </div>
          ) : null
        }
        tab={{ id: data.id, label: data.name }}
        sourceText={data.chunk.content}
      />
    );
  }

  if (error) return <DrawerErrorState label="thread file" />;
  if (isLoading || !data) {
    return (
      <div className="my-20 flex h-full w-full items-center justify-center">
        <Icon className="lucide--loader-2 animate-spin duration-700" />
      </div>
    );
  }

  return (
    <div className="relative h-full">
      <PDFViewer
        docId={data.id}
        fileUrl={data.url}
        fileMetadata={fileMetadata}
        highlights={[]}
        hideFeatureButtons={true}
        hideSnippingTool={true}
        fileName={data.name}
        threadFile={threadFile}
      />
    </div>
  );
}
